import { Injectable, inject, computed } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import type { Observable } from 'rxjs';
import { PORTFOLIO_REPOSITORY } from '../domain/repositories';
import type {
  Profile,
  Experience,
  Project,
  CaseStudy,
  TechnologyGroup,
  SiteConfig,
  SectionConfig,
  SocialLink,
  Education,
  Course,
  Certification,
  GitHubRepo,
  BootstrapData,
} from '../domain/entities';

/**
 * Portfolio façade.
 *
 * Exposes rxResource-based reactive signals for all portfolio data
 * (profile, experiences, technologies, etc.) with built-in loading,
 * error, and caching states.
 *
 * Performance optimization: Initial landing data is fetched in a single
 * "bootstrap" call to reduce latency and roundtrips.
 */
@Injectable({ providedIn: 'root' })
export class PortfolioService {
  private readonly repo = inject(PORTFOLIO_REPOSITORY);

  // --- rxResource master bootstrap ---

  readonly bootstrap = rxResource<BootstrapData, void>({
    stream: () => this.repo.getBootstrapData(),
  });

  // --- Pull-based observable API (delegates to repository) ---

  public getProfile(): Observable<Profile> {
    return this.repo.getProfile();
  }

  public getExperiences(): Observable<Experience[]> {
    return this.repo.getExperiences();
  }

  public getTechnologies(): Observable<TechnologyGroup[]> {
    return this.repo.getTechnologies();
  }

  public getFeaturedProjects(): Observable<Project[]> {
    return this.repo.getFeaturedProjects();
  }

  public getEducation(): Observable<{ education: Education[]; courses: Course[] }> {
    return this.repo.getEducation();
  }

  public getSiteConfig(): Observable<SiteConfig> {
    return this.repo.getSiteConfig();
  }

  public getSections(): Observable<SectionConfig[]> {
    return this.repo.getSections();
  }

  public getSocialLinks(): Observable<SocialLink[]> {
    return this.repo.getSocialLinks();
  }

  public getCertifications(): Observable<Certification[]> {
    return this.repo.getCertifications();
  }

  public getGithubRepos(): Observable<GitHubRepo[]> {
    return this.repo.getGithubRepos();
  }

  public getAllProjects(): Observable<Project[]> {
    return this.repo.getAllProjects();
  }

  public getProjectBySlug(slug: string): Observable<Project> {
    return this.repo.getProjectBySlug(slug);
  }

  public getCaseStudy(slug: string): Observable<CaseStudy> {
    return this.repo.getCaseStudy(slug);
  }

  public getCvUrl(): Observable<{ url: string }> {
    return this.repo.getCvUrl();
  }

  public getCertificationById(id: string): Observable<Certification> {
    return this.repo.getCertificationById(id);
  }

  // --- rxResource signal API ---
  // Derived from the master bootstrap call to prevent redundant network requests.

  readonly profile = {
    value: computed(() => this.bootstrap.value()?.profile),
    isLoading: this.bootstrap.isLoading,
    error: this.bootstrap.error,
  };

  readonly experiences = {
    value: computed(() => this.bootstrap.value()?.experiences),
    isLoading: this.bootstrap.isLoading,
    error: this.bootstrap.error,
  };

  readonly technologies = {
    value: computed(() => this.bootstrap.value()?.technologies),
    isLoading: this.bootstrap.isLoading,
    error: this.bootstrap.error,
  };

  readonly featuredProjects = {
    value: computed(() => this.bootstrap.value()?.featuredProjects),
    isLoading: this.bootstrap.isLoading,
    error: this.bootstrap.error,
  };

  readonly education = {
    value: computed(() => this.bootstrap.value()?.education),
    isLoading: this.bootstrap.isLoading,
    error: this.bootstrap.error,
  };

  readonly siteConfig = {
    value: computed(() => this.bootstrap.value()?.siteConfig),
    isLoading: this.bootstrap.isLoading,
    error: this.bootstrap.error,
  };

  readonly sections = {
    value: computed(() => this.bootstrap.value()?.sections),
    isLoading: this.bootstrap.isLoading,
    error: this.bootstrap.error,
  };

  readonly socialLinks = {
    value: computed(() => this.bootstrap.value()?.socialLinks),
    isLoading: this.bootstrap.isLoading,
    error: this.bootstrap.error,
  };

  readonly certifications = {
    value: computed(() => this.bootstrap.value()?.certifications),
    isLoading: this.bootstrap.isLoading,
    error: this.bootstrap.error,
  };

  /** GitHub repos are NOT part of bootstrap because they are fetched from external API (Github) via our backend */
  readonly githubRepos = rxResource<GitHubRepo[], void>({
    stream: () => this.repo.getGithubRepos(),
  });
}
